import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";

export const REASON_MAX = 140;

/**
 * Confirm a moderation action (ban / suspend) with an optional short note
 * the admin can jot down for future reference.
 */
export default function ModReasonDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  placeholder = "e.g. mass-flagging competitor listings",
  busy,
  onConfirm,
}) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="mod-reason-dialog"
        className="bg-[#0A0A0A] border-[#27272A] text-[#F3F4F6] sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">{title}</DialogTitle>
          <DialogDescription className="text-[#9CA3AF] text-xs whitespace-pre-line">
            {description}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <label className="text-[10px] uppercase tracking-wider text-[#6B7280]">
            Reason note <span className="normal-case tracking-normal">(optional)</span>
          </label>
          <Textarea
            data-testid="mod-reason-input"
            value={reason}
            maxLength={REASON_MAX}
            onChange={(e) => setReason(e.target.value)}
            placeholder={placeholder}
            className="bg-[#121212] border-[#27272A] text-sm text-[#F3F4F6] placeholder:text-[#4B5563] min-h-[72px] resize-none focus-visible:ring-[#2563EB]"
          />
          <div className="text-right text-[10px] text-[#6B7280]">{reason.length}/{REASON_MAX}</div>
        </div>
        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="mod-reason-cancel"
            className="h-9 rounded-full text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-[#1E1E1E] text-xs"
          >
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(reason.trim())}
            disabled={busy}
            data-testid="mod-reason-confirm"
            className="h-9 rounded-full bg-[#EF4444] hover:bg-[#DC2626] text-white text-xs"
          >
            {busy && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
